"use client";

import { useEffect } from "react";

const CHATGPT_PIXEL_ID = "TuBdsswYJLyH88KgC3C7bK";

declare global {
  interface Window {
    oaiq?: ((...args: any[]) => void) & { q?: any[] };
  }
}

// Bootstraps the ChatGPT Ads (OpenAI) `oaiq` queue and downloads the SDK.
// The `<script>` tag is `async` so it never blocks parsing or paint; the
// `window.oaiq` queue stub is set up synchronously inside this effect so any
// caller that fires `oaiq("measure", ...)` (e.g. `subscription_created` on
// `/success`) hits a real queue instead of `undefined`.
function initChatGptPixel() {
  if (typeof window === "undefined") return;
  if (window.oaiq) return;

  const q: ((...args: any[]) => void) & { q?: any[] } = function (
    ...args: any[]
  ) {
    q.q!.push(args);
  };
  q.q = [];
  window.oaiq = q;

  const script = document.createElement("script");
  script.async = true;
  script.src = "https://bzrcdn.openai.com/sdk/oaiq.min.js";
  const first = document.getElementsByTagName("script")[0];
  if (first?.parentNode) {
    first.parentNode.insertBefore(script, first);
  } else {
    // Fallback: HTML may not have a <script> tag yet (e.g. body still
    // streaming). Append directly to <head> so we don't throw.
    document.head.appendChild(script);
  }

  window.oaiq("init", {
    pixelId: CHATGPT_PIXEL_ID,
    debug: process.env.NODE_ENV !== "production",
  });
}

// Loads on mount, deferred to browser idle time when supported. This pairs
// with the `async` `<script>` tag inside `initChatGptPixel()` to keep the
// SDK off the LCP critical path while still guaranteeing it loads shortly
// after the page settles — never gated on user interaction, so server-
// redirect pages like `/success` (no clicks before tracking fires) still get
// the queue stubs in time.
function scheduleInit() {
  if (typeof window === "undefined") return;
  const ric = (window as Window & {
    requestIdleCallback?: (cb: () => void) => void;
  }).requestIdleCallback;
  if (ric) {
    ric(initChatGptPixel);
  } else {
    setTimeout(initChatGptPixel, 0);
  }
}

export default function ChatGptPixel() {
  useEffect(() => {
    scheduleInit();
  }, []);

  return null;
}
